import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { restrictPermissionsToModules } from '@constant/assistant-access-modules';
import { User } from '@models/user.model';
import { CheckPlatformService } from '@services/helpers/check-platform.service';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable } from 'rxjs';
import { delay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ReduxService } from '../state-management/redux.service';
import { ActingAsService } from './acting-as.service';
import { ApiService } from './api/api.service';
import { ConfigCacheService } from './api/config-cache.service';
import { DashboardService } from './api/dashboard.service';
import { Crypto } from './helpers/encryption-utils';
import { TokenService } from './helpers/token.service';
import { SidebarMenuService } from './sidebar-menu.service';
import { ActivityTrackerService } from './user-activity/activity-tracker.service';
import { WS } from './util/WebSocket.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  changeOffice: EventEmitter<string> = new EventEmitter();
  changeOfficeId: EventEmitter<string> = new EventEmitter();
  private loggedIn = new BehaviorSubject<boolean>(this.tokenService.isUserloggedIn());
  authStatus = this.loggedIn.asObservable();
  public UserSub = new BehaviorSubject<any>(this.tokenService.isUserloggedIn());
  AuthUser = this.loggedIn.asObservable();
  public baseUrl = environment.nodeApiUrl + '/' + environment.api_prefix;

  changeAuthStatus(value: boolean) {
    this.loggedIn.next(value);
  }
  setAuthUser(User: any) {
    if (User._id) {
      this.localStorageService.SetItem('uid', Crypto.encrypt(User._id));
    } else {
      this.localStorageService.SetItem('uid', Crypto.encrypt(User.id));
    }
    let teamPortal = [];
    if (User?.teams?.length > 0) {
      User?.teams.forEach(x => {
        teamPortal.push(x?._id);
      });
      this.localStorageService.RemoveItem('teams_id');
      this.localStorageService.SetItem('teams_id', Crypto.encrypt(teamPortal));
    }
    if (User?.companies?.length > 0) {
      this.localStorageService.SetItem('company', Crypto.encrypt(User?.companies));
    }
    if (User?.contact_type?.length > 0) {
      this.localStorageService.SetItem('contact_type', Crypto.encrypt(User?.contact_type || ''));
    }
    this.localStorageService.SetItem('roleid', Crypto.encrypt(User.role_id));
    this.localStorageService.SetItem('role', Crypto.encrypt(User.role.toLowerCase()));
    this.localStorageService.SetItem('officeid', Crypto.encrypt(User.active_office));
    this.localStorageService.SetItem('office_name', Crypto.encrypt(User.active_office_name));
    this.localStorageService.SetItem('user_name', Crypto.encrypt(User.full_name));
    this.localStorageService.SetItem('user_email', Crypto.encrypt(User.email || ''));
    let address = User.active_office_address_line_1 || '' + ', ' + User.active_office_address_line_2 || '';
    this.localStorageService.SetItem('office_address', Crypto.encrypt(address));
    if (User && User.global_office) {
      this.localStorageService.SetItem('isGlobalOffice', Crypto.encrypt('true'));
    } else {
      this.localStorageService.SetItem('isGlobalOffice', Crypto.encrypt('false'));
    }
    // While a Sales Assistant acts as this agent, `/me` returns the agent's full permission set —
    // narrow it to the modules the agent granted so route guards and *ngxPermissions hide the rest.
    // The backend must enforce the same scope; this only keeps the UI honest.
    this.actingAs.syncAllowedModules(User);
    const allowedModules = this.actingAs.getAllowedModules();
    if (allowedModules && User.permissions) {
      User.permissions.data = restrictPermissionsToModules(User.permissions.data, allowedModules);
    }
    this.permissionsService.loadPermissions(User.permissions.data);
    this.localStorageService.SetItem('_per', Crypto.encrypt(User.permissions.data));
    this.UserSub.next(User);
    this.sidebarMenuService.getSidebarMenuItemsList();
  }
  constructor(
    private tokenService: TokenService,
    private api: ApiService,
    private http: HttpClient,
    private router: Router,
    private redux: ReduxService,
    private localStorageService: LocalStorageService,
    public permissionsService: NgxPermissionsService,
    private toast: ToastrService,
    private _webSocket: WS,
    public sidebarMenuService: SidebarMenuService,
    private dashboardService: DashboardService,
    private checkPlatform: CheckPlatformService,
    private configCacheService: ConfigCacheService,
    private activityTracker: ActivityTrackerService,
    private actingAs: ActingAsService
  ) {}

  login(data) {
    return this.http.post(`${this.baseUrl}/auth/login`, data);
  }

  changeUserOffice(text: string) {
    this.changeOffice.next(text);
  }

  getChangeUserOffice() {
    return this.changeOffice;
  }

  updateOfficeId(officeId: string) {
    this.redux.setTagSetting(null);
    // Session-cached config data (see ConfigCacheInterceptor) is scoped to the office that was
    // active when it was fetched — none of it belongs to the office being switched into.
    this.configCacheService.clear();
    this.changeOfficeId.next(officeId);
  }

  getOfficeId() {
    return this.changeOfficeId;
  }

  sendPasswordResetLink(data) {
    return this.http.post(`${this.baseUrl}/sendPasswordResetLink`, data);
  }

  changePassword(data) {
    return this.http.post(`${this.baseUrl}/resetPassword`, data);
  }

  public logOut() {
    this.api.post('/auth/logout', {}).subscribe({
      next: _ => {
        this.activityTracker.stop();
        this.applyLogoutSideEffects();
        return this.router.navigateByUrl('/login');
      },
      error: _ => {
        this.toast.error('Something wrong');
      },
    });
  }

  public logOutWithoutToken() {
    // Before the side effects: the session-ending beacon needs the token they clear.
    this.activityTracker.stop('timeout');
    this.applyLogoutSideEffects();
    this.router.navigateByUrl('/login');
  }

  /** Clears tokens, in-memory API/store caches, permissions, session storage, and Cache Storage (PWA). */
  private applyLogoutSideEffects(): void {
    this.tokenService.removeToken();
    this.api.clearClientCaches();
    this.configCacheService.clear();
    this.dashboardService.clearDashboardCaches();
    this.sidebarMenuService.clearMenuCache();
    this._webSocket.destroyPeer();
    this.redux.clearStoreCachesOnLogout();
    this.permissionsService.flushPermissions();
    this.clearSessionAndBrowserCaches();
  }

  private clearSessionAndBrowserCaches(): void {
    if (!this.checkPlatform.isBrowser()) {
      return;
    }
    try {
      sessionStorage.clear();
    } catch {
      /* ignore */
    }
    if ('caches' in window) {
      void caches
        .keys()
        .then(names => Promise.all(names.map(n => caches.delete(n))))
        .catch(() => undefined);
    }
  }

  public loadPermissions() {
    let permissions: any;
    permissions = this.localStorageService.GetItem('_per');
    if (permissions) {
      this.permissionsService.loadPermissions(Crypto.decrypt(permissions));
    }
  }

  getUser(): Observable<User[]> {
    return this.http.get<User[]>(`${this.baseUrl}/me?include=offices,permissions,groups`).pipe(delay(1000));
  }

  forgetAccountPassword(payload: any) {
    return this.api.post('/auth/forget-password', payload);
  }
  checkTermsAndConditionValidations(payload: any) {
    return this.api.post('/open-user-tc', payload);
  }
}
